import {isLoggedIn} from '@github-ui/client-env'
import {usePinnedComment} from '@github-ui/commenting/usePinnedComment'
import {copyText} from '@github-ui/copy-to-clipboard'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {userHovercardPath} from '@github-ui/paths'
import {ProfileReference} from '@github-ui/profile-reference'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {ArrowDownIcon, KebabHorizontalIcon, LinkIcon, PinIcon, PinSlashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link, RelativeTime} from '@primer/react'
import {Suspense, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {PinnedCommentDisplay$data, PinnedCommentDisplay$key} from './__generated__/PinnedCommentDisplay.graphql'
import styles from './PinnedCommentDisplay.module.css'

export type PinnedCommentDisplayProps = {
  issue: PinnedCommentDisplay$key
}

export function PinnedCommentDisplay({issue}: PinnedCommentDisplayProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment PinnedCommentDisplay on Issue {
        pinnedIssueComment {
          pinnedBy {
            login
          }
          issueComment {
            id
            shortBodyHTML
            createdAt
            url
            viewerCanUnpin
            author {
              login
              avatarUrl
              profileUrl
            }
          }
        }
      }
    `,
    issue,
  )

  if (!data.pinnedIssueComment) {
    return null
  }

  const {issueComment: comment, pinnedBy} = data.pinnedIssueComment

  return (
    <Suspense fallback={null}>
      <div className={styles.PinnedCommentContainer} data-testid="pinned-comment-display">
        <Header pinnedBy={pinnedBy} commentId={comment.id} viewerCanUnpin={comment.viewerCanUnpin} url={comment.url} />
        {comment.author && <AuthorAttribution comment={comment} />}
        <VerifiedHTMLDiv
          className={styles.PinnedCommentBody}
          html={comment.shortBodyHTML as SafeHTMLString}
          data-testid="pinned-comment-body"
        />
        <div className={styles.CommentLinkContainer}>
          <Link href={comment.url} className={styles.PinnedByLink}>
            View full comment <ArrowDownIcon size={16} />
          </Link>
        </div>
      </div>
    </Suspense>
  )
}

type HeaderProps = {
  pinnedBy: {
    login: string
  }
  commentId: string
  viewerCanUnpin: boolean
  url: string
}

type MenuAction = {
  key: string
  label: string
  icon: React.ComponentType
  onSelect: () => void
  ariaLabel: string
  visible: boolean
}

function Header({pinnedBy, commentId, viewerCanUnpin, url}: HeaderProps) {
  const {unpinComment} = usePinnedComment()
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const handleUnpin = () => {
    unpinComment(commentId)
  }

  const handleCopyLink = () => {
    copyText(url)
  }

  const menuActions: MenuAction[] = [
    {
      key: 'copy-link',
      label: 'Copy link',
      icon: LinkIcon,
      onSelect: handleCopyLink,
      ariaLabel: 'Copy link to pinned comment',
      visible: true,
    },
    {
      key: 'unpin',
      label: 'Unpin',
      icon: PinSlashIcon,
      onSelect: handleUnpin,
      ariaLabel: 'Unpin comment',
      visible: isLoggedIn() && viewerCanUnpin,
    },
  ]

  const visibleActions = menuActions.filter(action => action.visible)

  return (
    <div className={styles.PinnedCommentHeader}>
      <div className={styles.PinnedBySection}>
        <PinIcon size={14} className={styles.PinnedByIcon} />
        Pinned by{' '}
        <Link
          href={`/${pinnedBy.login}`}
          data-hovercard-url={userHovercardPath({owner: pinnedBy.login})}
          className={styles.PinnedByLink}
        >
          <ProfileReference login={pinnedBy.login} isAgent={false} />
        </Link>
      </div>
      {visibleActions.length > 0 && (
        <ActionMenu anchorRef={menuButtonRef}>
          <ActionMenu.Anchor>
            <IconButton
              ref={menuButtonRef}
              size="small"
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label="Pinned comment options"
              className={styles.MenuButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay width="auto">
            <ActionList>
              {visibleActions.map(action => (
                <ActionList.Item key={action.key} onSelect={action.onSelect} aria-label={action.ariaLabel}>
                  <ActionList.LeadingVisual>
                    <action.icon />
                  </ActionList.LeadingVisual>
                  {action.label}
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
    </div>
  )
}

type AuthorAttributionProps = {
  comment: NonNullable<PinnedCommentDisplay$data['pinnedIssueComment']>['issueComment']
}

function AuthorAttribution({comment}: AuthorAttributionProps) {
  const createdAt = new Date(comment.createdAt)
  const author = comment.author!

  return (
    <div className={styles.CommentAuthorInfo}>
      <Link
        href={author.profileUrl ?? undefined}
        data-hovercard-url={userHovercardPath({owner: author.login})}
        className={styles.AuthorLink}
      >
        <GitHubAvatar className={styles.Avatar} src={author.avatarUrl} size={25} alt={`@${author.login}`} />
        {author.login}
      </Link>
      <Link href={comment.url} data-testid="issue-body-header-link" className={styles.DateLink}>
        <RelativeTime date={createdAt}>
          on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
        </RelativeTime>
      </Link>
    </div>
  )
}

try{ PinnedCommentDisplay.displayName ||= 'PinnedCommentDisplay' } catch {}
try{ Header.displayName ||= 'Header' } catch {}
try{ AuthorAttribution.displayName ||= 'AuthorAttribution' } catch {}